import React, { useState } from 'react';
import {
  Table,
  Button,
  Input,
  Select,
  DatePicker,
  Space,
  Tag,
  Progress,
  Dropdown,
  Empty,
  Typography,
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  LockOutlined,
  UnlockOutlined,
  MoreOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import { DesignTokens, getCapacityColor } from './DesignTokens';

const { RangePicker } = DatePicker;
const { Title } = Typography;

export interface Tenant {
  id: number;
  name: string;
  code: string;
  status: 'normal' | 'frozen';
  capacity: number; // 容量使用百分比
  createdAt: string;
}

export interface TenantListProps {
  data?: Tenant[];
  loading?: boolean;
  onAdd?: () => void;
  onEdit?: (id: number) => void;
  onDelete?: (id: number) => void;
  onFreeze?: (id: number) => void;
  onUnfreeze?: (id: number) => void;
  onSearch?: (params: SearchParams) => void;
}

export interface SearchParams {
  status?: string;
  dateRange?: [Dayjs, Dayjs];
  keyword?: string;
}

export const TenantList: React.FC<TenantListProps> = ({
  data = [],
  loading = false,
  onAdd,
  onEdit,
  onDelete,
  onFreeze,
  onUnfreeze,
  onSearch,
}) => {
  const [searchParams, setSearchParams] = useState<SearchParams>({});

  const handleSearch = () => {
    onSearch?.(searchParams);
  };

  const handleReset = () => {
    setSearchParams({});
    onSearch?.({});
  };

  const columns: ColumnsType<Tenant> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      align: 'center',
      sorter: true,
    },
    {
      title: '租户名称',
      dataIndex: 'name',
      key: 'name',
      width: 160,
      sorter: true,
    },
    {
      title: '租户代码',
      dataIndex: 'code',
      key: 'code',
      width: 120,
      align: 'center',
      sorter: true,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      align: 'center',
      sorter: true,
      render: (status: string) => {
        const isNormal = status === 'normal';
        return (
          <Tag
            color={isNormal ? 'success' : 'error'}
            style={{
              borderRadius: DesignTokens.borderRadius.sm,
              fontSize: 14,
            }}
          >
            <span style={{ 
              display: 'inline-block', 
              width: 8, 
              height: 8, 
              borderRadius: '50%',
              backgroundColor: isNormal ? DesignTokens.colors.success : DesignTokens.colors.error,
              marginRight: 4,
            }} />
            {isNormal ? '正常' : '冻结'}
          </Tag>
        );
      },
    },
    {
      title: '容量使用',
      dataIndex: 'capacity',
      key: 'capacity',
      width: 140,
      align: 'center',
      render: (capacity: number) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Progress
            percent={capacity}
            size="small"
            strokeColor={getCapacityColor(capacity)}
            showInfo={false}
            style={{ flex: 1 }}
          />
          <span style={{ 
            color: getCapacityColor(capacity),
            fontSize: 12,
            fontWeight: 500,
          }}>
            {capacity}%
          </span>
        </div>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      align: 'center',
      sorter: true,
      render: (date: string) => dayjs(date).format('YYYY-MM-DD'),
    },
    {
      title: '操作',
      key: 'actions',
      width: 200,
      align: 'center',
      render: (_, record) => (
        <Space size="middle">
          <Button
            data-testid={`tenant-btn-edit-${record.id}`}
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => onEdit?.(record.id)}
          >
            编辑
          </Button>
          <Button
            data-testid={`tenant-btn-delete-${record.id}`}
            type="link"
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => onDelete?.(record.id)}
          >
            删除
          </Button>
          {record.status === 'normal' ? (
            <Button
              data-testid={`tenant-btn-freeze-${record.id}`}
              type="link"
              size="small"
              icon={<LockOutlined />}
              onClick={() => onFreeze?.(record.id)}
            >
              冻结
            </Button>
          ) : (
            <Button
              data-testid={`tenant-btn-unfreeze-${record.id}`}
              type="link"
              size="small"
              icon={<UnlockOutlined />}
              onClick={() => onUnfreeze?.(record.id)}
            >
              解冻
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: DesignTokens.spacing.xl }}>
      {/* 顶部操作栏 */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: DesignTokens.spacing.xl,
          height: 56,
        }}
      >
        <div>
          <Title level={2} style={{ margin: 0, ...DesignTokens.typography.h2 }}>
            租户管理
          </Title>
          <div style={{ 
            ...DesignTokens.typography.caption, 
            color: DesignTokens.colors.gray7,
            marginTop: 4,
          }}>
            平台管理 / 租户管理
          </div>
        </div>
        <Button
          data-testid="tenant-btn-add"
          type="primary"
          icon={<PlusOutlined />}
          onClick={onAdd}
          style={{ height: 32 }}
        >
          新建租户
        </Button>
      </div>

      {/* 筛选栏 */}
      <div
        style={{
          background: DesignTokens.colors.gray2,
          padding: `${DesignTokens.spacing.md}px ${DesignTokens.spacing.xl}px`,
          marginBottom: DesignTokens.spacing.lg,
          borderRadius: DesignTokens.borderRadius.md,
        }}
      >
        <Space size="middle" wrap>
          <div>
            <label style={{ ...DesignTokens.typography.bodyRegular, color: DesignTokens.colors.gray8, marginRight: 8 }}>
              租户状态
            </label>
            <Select
              data-testid="tenant-select-status"
              placeholder="全部"
              style={{ width: 120 }}
              value={searchParams.status}
              onChange={(value) => setSearchParams({ ...searchParams, status: value })}
              options={[
                { label: '全部', value: undefined },
                { label: '正常', value: 'normal' },
                { label: '冻结', value: 'frozen' },
              ]}
            />
          </div>
          <div>
            <label style={{ ...DesignTokens.typography.bodyRegular, color: DesignTokens.colors.gray8, marginRight: 8 }}>
              创建时间
            </label>
            <RangePicker
              data-testid="tenant-datepicker-created-at"
              style={{ width: 240 }}
              value={searchParams.dateRange}
              onChange={(dates) => setSearchParams({ ...searchParams, dateRange: dates as [Dayjs, Dayjs] })}
            />
          </div>
          <Input
            data-testid="tenant-input-search"
            placeholder="搜索租户名称/代码"
            prefix={<SearchOutlined style={{ color: DesignTokens.colors.gray7 }} />}
            style={{ width: 300 }}
            value={searchParams.keyword}
            onChange={(e) => setSearchParams({ ...searchParams, keyword: e.target.value })}
            onPressEnter={handleSearch}
          />
          <Button data-testid="tenant-btn-search" type="primary" onClick={handleSearch}>
            搜索
          </Button>
          <Button data-testid="tenant-btn-reset" onClick={handleReset}>
            重置
          </Button>
        </Space>
      </div>

      {/* 数据表格 */}
      <Table
        data-testid="tenant-table"
        columns={columns}
        dataSource={data}
        loading={loading}
        rowKey="id"
        pagination={{
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => `共 ${total} 条`,
          pageSizeOptions: ['10', '20', '50', '100'],
        }}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="暂无租户数据"
              style={{ padding: '60px 0' }}
            >
              <Button data-testid="tenant-btn-add-empty" type="primary" icon={<PlusOutlined />} onClick={onAdd}>
                创建第一个租户
              </Button>
            </Empty>
          ),
        }}
        style={{
          background: DesignTokens.colors.gray1,
          borderRadius: DesignTokens.borderRadius.lg,
        }}
      />
    </div>
  );
};
